'use client'

import { ReactNode } from 'react'

import { cn } from '@/lib/utils'
import LcIcon from '@/components/livecontact/icons'

type IPhone15ProProps = {
  children: ReactNode
  className?: string
  /** Status-bar glyph colour. The contact sheet's wallpaper is light at the top, so dark wins. */
  statusBarTone?: 'light' | 'dark'
}

/**
 * iOS status bar (62pt tall, ≈46px at this screen size): the time centred in the left ear, the
 * signal / Wi-Fi / battery cluster centred in the right one, both cleared by the Dynamic Island.
 * The time is Apple's canonical 9:41 rather than the live clock, as in the design.
 */
const StatusBar = ({ tone }: { tone: 'light' | 'dark' }) => (
  <div
    aria-hidden
    className={cn(
      'pointer-events-none absolute inset-x-0 top-0 z-30 flex h-[46px] items-center pt-[3px]',
      tone === 'light' ? 'text-white' : 'text-black'
    )}
  >
    <span className="flex-1 text-center text-[12.7px] font-semibold leading-none tracking-[-0.2px]">
      9:41
    </span>

    {/* Dynamic Island keep-out */}
    <span className="h-full w-[94px] shrink-0" />

    <span className="flex flex-1 items-center justify-center gap-[4.5px]">
      {/* Cellular */}
      <span className="flex items-end gap-[1.2px]">
        {[3.5, 5, 6.5, 8].map((height) => (
          <span key={height} className="w-[2.5px] rounded-[0.8px] bg-current" style={{ height }} />
        ))}
      </span>
      <LcIcon name="wifi" size={12} strokeWidth={2.2} />
      {/* Battery */}
      <span className="relative h-[10px] w-[20px]">
        <span className="absolute inset-0 rounded-[3px] border border-current opacity-35" />
        <span className="absolute inset-[1.8px] right-[5px] rounded-[1.5px] bg-current" />
        <span className="absolute -right-[2.5px] top-[3px] h-[4px] w-[1.5px] rounded-r-[1px] bg-current opacity-35" />
      </span>
    </span>
  </div>
)

/**
 * iPhone 15 Pro frame (320×635): dark titanium body, black bezel, Dynamic Island and the system
 * status bar. Children own their own scrolling so the frame just clips.
 */
const IPhone15Pro = ({ children, className = '', statusBarTone = 'dark' }: IPhone15ProProps) => {
  return (
    <div className={cn('lc-asset-preview relative mx-auto', className)}>
      <div className="relative h-[635px] w-[320px] rounded-[50px] bg-[#2C2C2E] p-[3px] shadow-pop">
        <div className="relative h-full w-full rounded-[47px] bg-black p-[7px]">
          {/* Screen */}
          <div className="relative h-full w-full overflow-hidden rounded-[40px] bg-surface">
            <StatusBar tone={statusBarTone} />

            {/* Dynamic Island — 125×36.7pt, 11pt below the screen edge */}
            <div className="absolute left-1/2 top-[11px] z-40 h-[27px] w-[94px] -translate-x-1/2 rounded-full bg-black">
              <span className="absolute right-[7px] top-1/2 size-[9px] -translate-y-1/2 rounded-full bg-[#0A0A12] ring-1 ring-white/[0.06]" />
            </div>

            <div className="h-full overflow-hidden">{children}</div>
          </div>
        </div>

        {/* Side buttons */}
        <div className="absolute -left-[2px] top-[88px] h-7 w-[3px] rounded-l-[2px] bg-[#3A3A3C]" />
        <div className="absolute -left-[2px] top-[130px] h-12 w-[3px] rounded-l-[2px] bg-[#3A3A3C]" />
        <div className="absolute -left-[2px] top-[190px] h-12 w-[3px] rounded-l-[2px] bg-[#3A3A3C]" />
        <div className="absolute -right-[2px] top-[150px] h-16 w-[3px] rounded-r-[2px] bg-[#3A3A3C]" />
      </div>
    </div>
  )
}

export default IPhone15Pro
